import React, { useState, useEffect } from 'react'
import { ListGroup, Badge, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import Search from 'shared/forms/search'
import { filter, reject } from 'lodash'
import { useEmployees } from 'api/hooks/use-employees'
import { useQueryClient } from 'react-query'
import { useBulkCreateDashboardEmployees, useBulkDestroyDashboardEmployees } from 'api/hooks/use-dashboard-employees'
import Table from 'react-bootstrap/Table'
import { pick } from 'lodash'
import { map } from 'lodash'
import EmployeeItem from './item'
import DeleteEmployee from './delete'
import UpdateEmployee from './update'
import Spinner from 'react-bootstrap/Spinner'

function EmployeeSearch({ params, setParams, setShow, columnName, canEdit = true, showAsAccordion = false, isRequired = false, label = 'Employee(s)', restrictedOptions = [], initialSearch = '' }) {
  const queryClient = useQueryClient()
  const { mutate: bulkCreateEmployees } = useBulkCreateDashboardEmployees({
    options: {
      onSuccess: (data) => {
        setParams((prev) => {
          const updatedEmployees = (prev.employees || []).map((emp) => {
            const created = data.find((d) => d.employee_id === emp.id)
            return created ? { ...emp, dashboard_employee_id: created.id } : emp
          })
          return { ...prev, employees: updatedEmployees }
        })
        queryClient.invalidateQueries(`dashboards/${params.dashboardId}`)
      }
    }
  })
  const { mutate: bulkDestroyEmployees } = useBulkDestroyDashboardEmployees({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(`dashboards/${params.dashboardId}`)
      }
    }
  })
  const hasRestrictedOptions = restrictedOptions.length > 0

  const defaultSearchParams = {
    search: '',
    section: params.section_id,
    page: 1,
    perPage: 5
  }
  const [searchParams, setSearchParams] = useState(
    initialSearch
      ? { ...defaultSearchParams, search: initialSearch, perPage: 5 }
      : defaultSearchParams
  )
  const [loadAll, setLoadAll] = useState(false)

  // Reset loadAll when search term changes
  useEffect(() => {
    setLoadAll(false)
  }, [searchParams.search])

  // Apply initialSearch when it changes
  useEffect(() => {
    if (initialSearch) {
      setSearchParams((prev) => ({ ...prev, search: initialSearch, page: 1 }))
    }
  }, [initialSearch])
  const [isExpanded, setIsExpanded] = useState(isRequired)
  const allEmployees = (params.employees || []).concat(params.excludedEmployees || [])

  const addOrRemoveEmployee = (employee, dashboardEmployeeId = null, employeeColumnName = null) => {
    const effectiveColumnName = employeeColumnName || columnName
    const findEmployee = filter(allEmployees, (emp) =>
      emp.id === employee.id && (!effectiveColumnName || emp.database_column_name === effectiveColumnName)
    )
    if (findEmployee.length > 0) {
      const newList = reject(allEmployees, (emp) =>
        emp.id === employee.id && (!effectiveColumnName || emp.database_column_name === effectiveColumnName)
      )
      updateEmployees(newList)
      setSearchParams(defaultSearchParams)
      queryClient.invalidateQueries('employees')
      queryClient.invalidateQueries('transactions')
    } else {
      // Add employee to the list (staging for new dashboard creation)
      const newEmployee = {
        ...pick(employee, ['id', 'external_id', 'full_name', 'display_name']),
        dashboard_employee_id: dashboardEmployeeId,
        options: employee.options || 'include',
        database_column_name: employee.database_column_name || effectiveColumnName
      }
      const newList = [...allEmployees, newEmployee]
      updateEmployees(newList)
      setSearchParams(defaultSearchParams)
      queryClient.invalidateQueries('employees')
      queryClient.invalidateQueries('transactions')
    }
  }

  const onEmployeeCreated = (employee, dashboardEmployeeId, columnName) => {
    const newList = [
      ...allEmployees,
      {
        ...pick(employee, ['id', 'external_id', 'full_name', 'display_name']),
        dashboard_employee_id: dashboardEmployeeId,
        options: 'include',
        database_column_name: columnName
      }
    ]
    updateEmployees(newList)
    setSearchParams(defaultSearchParams)
    queryClient.invalidateQueries('employees')
    queryClient.invalidateQueries('transactions')
  }

  const toggleIncludeExclude = (employee, options) => {
    const newList = map(allEmployees, function (a) {
      return a.id === employee.id && a.database_column_name === employee.database_column_name
        ? { ...employee, options: options }
        : a
    })
    updateEmployees(newList)
    queryClient.invalidateQueries('employees')
    queryClient.invalidateQueries('time_periods')
    queryClient.invalidateQueries('transactions')
  }

  const updateEmployees = (newList) => {
    setParams((prev) => ({
      ...prev,
      employees: filter(newList, (emp) => emp.options === 'include'),
      excludedEmployees: filter(newList, (emp) => emp.options === 'exclude')
    }))
  }

  // Only fetch from API when no restricted options are provided
  // When restricted options exist, we filter client-side instead
  const effectiveSearchParams = loadAll
    ? { ...searchParams, perPage: 1000 }
    : searchParams
  const { isSuccess: isApiSuccess, isLoading: isApiLoading, data: apiData } = useEmployees({
    params: effectiveSearchParams,
    options: {
      enabled: !hasRestrictedOptions && searchParams.search !== '' && searchParams.section && searchParams.section.length > 2
    }
  })

  // Client-side fuzzy search for restricted options (case-insensitive string contains)
  const filteredRestrictedOptions = hasRestrictedOptions && searchParams.search
    ? restrictedOptions.filter((option) =>
        option.label.toLowerCase().includes(searchParams.search.toLowerCase())
      )
    : []

  // Determine loading/success states and data based on whether we're using restricted options
  const isLoading = !hasRestrictedOptions && isApiLoading
  const isSuccess = hasRestrictedOptions
    ? searchParams.search !== ''
    : isApiSuccess
  const data = hasRestrictedOptions
    ? { data: filteredRestrictedOptions.map((option) => ({ id: option.value, display_name: option.label, full_name: option.label })) }
    : apiData

  // Format employee value for display
  const formatEmployeeValue = () => {
    const employees = params.employees || []
    const excludedEmployees = params.excludedEmployees || []
    const filteredEmployees = columnName
      ? employees.filter(e => e.database_column_name === columnName)
      : employees
    const filteredExcludedEmployees = columnName
      ? excludedEmployees.filter(e => e.database_column_name === columnName)
      : excludedEmployees

    const includedCount = filteredEmployees.length
    const excludedCount = filteredExcludedEmployees.length

    if (includedCount === 0 && excludedCount === 0) {
      return 'Not set'
    }

    const parts = []
    if (includedCount > 0) {
      parts.push(`${includedCount} included`)
    }
    if (excludedCount > 0) {
      parts.push(`${excludedCount} excluded`)
    }
    return parts.join(', ')
  }

  const content = (
    <>
      <Search
        params={searchParams}
        setParams={setSearchParams}
        placeholder={'Search Employee'}
        disabled={!canEdit}
      />
      <ListGroup variant="flush">
        {isLoading && (
          <ListGroup.Item>
            <div className="text-muted text-center">
              <Spinner animation="grow" variant="warning" className="my-5" />
            </div>
          </ListGroup.Item>
        )}
        {isSuccess &&
          data.data
            .filter((e) => {
              // Filter out employees that already have a dashboard_employee record for this column
              return !allEmployees.some(
                (emp) => emp.id === e.id && (!columnName || emp.database_column_name === columnName)
              )
            })
            .map((e) => (
              <EmployeeItem
                key={`employee-option-${e.id}`}
                employee={e}
                params={params}
                dashboardId={params.dashboardId}
                addOrRemoveEmployee={addOrRemoveEmployee}
                onEmployeeCreated={onEmployeeCreated}
                columnName={columnName}
                allEmployees={allEmployees}
                canEdit={canEdit}
              />
            ))}
      </ListGroup>
      {canEdit && (() => {
        const selectedForColumn = allEmployees.filter((emp) => !columnName || emp.database_column_name === columnName)
        const unselectedResults = isSuccess
          ? data.data.filter((e) =>
              !allEmployees.some(
                (emp) => emp.id === e.id && (!columnName || emp.database_column_name === columnName)
              )
            )
          : []
        const hasMore = isSuccess && !loadAll && data.data.length >= searchParams.perPage
        const showButtons = unselectedResults.length > 0 || hasMore || selectedForColumn.length > 0
        return showButtons ? (
          <div className="d-flex gap-2 mt-2">
            {hasMore && (
              <Button
                variant="light-primary"
                size="sm"
                onClick={() => setLoadAll(true)}
              >
                Load All
              </Button>
            )}
            {unselectedResults.length > 1 && ['include', 'exclude'].map((option) => (
              <Button
                key={option}
                variant={option === 'include' ? 'outline-primary' : 'outline-warning'}
                size="sm"
                onClick={() => {
                  const newEmployees = unselectedResults.map((e) => ({
                    ...pick(e, ['id', 'external_id', 'full_name', 'display_name']),
                    dashboard_employee_id: null,
                    options: option,
                    database_column_name: e.database_column_name || columnName
                  }))
                  const newList = [...allEmployees, ...newEmployees]
                  updateEmployees(newList)
                  if (params.dashboardId) {
                    bulkCreateEmployees({
                      dashboardId: params.dashboardId,
                      employees: unselectedResults.map((e) => ({
                        id: e.id,
                        database_column_name: e.database_column_name || columnName
                      })),
                      options: option
                    })
                  }
                  queryClient.invalidateQueries('employees')
                  queryClient.invalidateQueries('transactions')
                  setSearchParams(defaultSearchParams)
                  setLoadAll(false)
                }}
              >
                {option === 'include' ? 'Include' : 'Exclude'} All ({unselectedResults.length})
              </Button>
            ))}
            {selectedForColumn.length > 0 && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => {
                  if (params.dashboardId) {
                    bulkDestroyEmployees({ dashboardId: params.dashboardId, databaseColumnName: columnName })
                  }
                  const remaining = allEmployees.filter((emp) => columnName && emp.database_column_name !== columnName)
                  updateEmployees(remaining)
                  queryClient.invalidateQueries('employees')
                  queryClient.invalidateQueries('transactions')
                }}
              >
                Clear All ({selectedForColumn.length})
              </Button>
            )}
          </div>
        ) : null
      })()}

      <Table size="sm" className="mt-4">
        <thead>
          <tr>
            <th>Name/ID</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {allEmployees.filter((employee) => !columnName || employee.database_column_name === columnName).length === 0 && (
            <tr>
              <td colSpan="3" className="text-center">
                No employees selected. Search for and select an employee above
                to include or exclude from the view.
              </td>
            </tr>
          )}
          {allEmployees
            .filter((employee) => !columnName || employee.database_column_name === columnName)
            .map((employee) => {
              return (
            <tr key={`dashboard-employee-${employee.id}-${employee.database_column_name}`}>
              <td>
                <div className="mt-1">{employee.display_name}</div>
              </td>
              <td>
                <UpdateEmployee
                  toggleIncludeExclude={toggleIncludeExclude}
                  employee={employee}
                  dashboardId={params.dashboardId}
                  canEdit={canEdit}
                />
              </td>
              <td>
                <DeleteEmployee
                  addOrRemoveEmployee={addOrRemoveEmployee}
                  employee={employee}
                  dashboardId={params.dashboardId}
                  canEdit={canEdit}
                />
              </td>
            </tr>
          )})}
        </tbody>
      </Table>
    </>
  )

  if (showAsAccordion) {
    return (
      <div className="mb-2">
        {/* Accordion Header */}
        <div
          className={`d-flex justify-content-between align-items-center p-3 border rounded ${
            isRequired ? 'border-warning' : ''
          }`}
          style={{ cursor: 'pointer', backgroundColor: isExpanded ? '#f8f9fa' : 'white' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            <div className="d-flex align-items-center gap-2">
              <strong>{label}</strong>
              {isRequired && (
                <Badge bg="warning" text="dark" className="small">
                  Required
                </Badge>
              )}
            </div>
            {!isExpanded && (
              <div className="text-muted small">{formatEmployeeValue()}</div>
            )}
          </div>
          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="text-muted" />
        </div>

        {/* Accordion Content */}
        {isExpanded && (
          <div className="p-3 border border-top-0 rounded-bottom">
            {content}
          </div>
        )}
      </div>
    )
  }

  return content
}

export default EmployeeSearch
